import React, { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from "../lib/utils";
import CardDisplay from './CardDisplay';
import RangeSlider from './RangeSlider';
import { getCardImageUrl } from '../utils/imageCache';
import { filterAndSortCards } from './cardCatalog';

const CardBrowser = ({ cards: initialCards = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomains, setSelectedDomains] = useState([]); // Multi-select for domains
  const [selectedRarities, setSelectedRarities] = useState([]); // Multi-select for rarities
  const [selectedType, setSelectedType] = useState('All');
  const [selectedSet, setSelectedSet] = useState('All');
  const [selectedSupertype, setSelectedSupertype] = useState('All');
  const [selectedVariant, setSelectedVariant] = useState('All');
  const [sortBy, setSortBy] = useState('name'); // Default sort by name
  const [sortOrder, setSortOrder] = useState('asc'); // asc or desc
  const [energyRange, setEnergyRange] = useState([0, 12]);
  const [powerRange, setPowerRange] = useState([0, 4]);
  const [mightRange, setMightRange] = useState([0, 10]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const closeDialogButtonRef = useRef(null);
  const cardsPerPage = 50;

  // Get unique values for filters
  const uniqueSets = useMemo(() => {
    // Ensure we have a valid array before mapping
    const cardsToUse = initialCards && Array.isArray(initialCards) ? initialCards : [];
    const sets = [...new Set(cardsToUse.map(card => card?.set_label).filter(Boolean))];
    return sets.sort();
  }, [initialCards]);

  const [uniqueSupertypes, setUniqueSupertypes] = useState([]);

  // Fetch unique supertypes from the backend
  useEffect(() => {
    const fetchSupertypes = async () => {
      try {
        const response = await fetch('/api/supertypes');
        if (response.ok) {
          const supertypes = await response.json();
          setUniqueSupertypes(supertypes);
        } else {
          console.error('Failed to fetch supertypes');
          setUniqueSupertypes([]);
        }
      } catch (error) {
        console.error('Error fetching supertypes:', error);
        setUniqueSupertypes([]);
      }
    };

    fetchSupertypes();
  }, []);

  useEffect(() => {
    if (!selectedCard) return undefined;
    closeDialogButtonRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setSelectedCard(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedCard]);

  const filteredCards = useMemo(() => filterAndSortCards(initialCards, {
    searchTerm,
    selectedDomains,
    selectedRarities,
    selectedType,
    selectedSet,
    selectedSupertype,
    selectedVariant,
    sortBy,
    sortOrder,
    energyRange,
    powerRange,
    mightRange,
  }), [
    initialCards,
    searchTerm,
    selectedDomains,
    selectedRarities,
    selectedType,
    selectedSet,
    selectedSupertype,
    selectedVariant,
    sortBy,
    sortOrder,
    energyRange,
    powerRange,
    mightRange,
  ]);
  const totalPages = Math.max(1, Math.ceil(filteredCards.length / cardsPerPage));
  const visiblePage = Math.min(currentPage, totalPages);
  const cardsToShow = filteredCards.slice(
    (visiblePage - 1) * cardsPerPage,
    visiblePage * cardsPerPage
  );
  const resetPage = () => setCurrentPage(1);

  return (
    <div className="w-full">
      <div className="space-y-4 mb-6">
        {/* First row: Search bar and sorting controls */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <input
              type="text"
              aria-label="Search cards"
              placeholder="Search cards..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                resetPage();
              }}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
            />
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <select
                aria-label="Sort cards by"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  resetPage();
                }}
                className="w-full sm:w-auto px-4 py-2 bg-background border border-border rounded-lg text-foreground"
              >
                <option value="id">ID</option>
                <option value="name">Name</option>
                <option value="energy">Energy</option>
                <option value="power">Power</option>
                <option value="might">Might</option>
              </select>
              <button
                onClick={() => {
                  setSortOrder((order) => order === 'asc' ? 'desc' : 'asc');
                  resetPage();
                }}
                className="w-full sm:w-auto px-4 py-2 bg-background border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
              >
                {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              </button>
            </div>
          </div>
        </div>

        {/* Second row: Domain and Rarity filters */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Domain filter as image buttons */}
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-muted-foreground">Domains:</span>
              {selectedDomains.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedDomains([]);
                    resetPage();
                  }}
                  className="text-xs px-2 py-1 bg-muted rounded-lg hover:bg-muted/80"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                aria-label="All domains"
                aria-pressed={selectedDomains.length === 0}
                className={`relative size-8 rounded-full overflow-hidden border-2 transition-all ${
                  selectedDomains.length === 0
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-transparent hover:border-border'
                }`}
                onClick={() => {
                  setSelectedDomains([]);
                  resetPage();
                }}
              >
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-xs">All</span>
                </div>
              </button>
              {['Fury', 'Calm', 'Mind', 'Body', 'Chaos', 'Order'].map((domain) => (
                <button
                  key={domain}
                  type="button"
                  aria-label={domain}
                  aria-pressed={selectedDomains.includes(domain)}
                  className={`relative size-8 rounded-full overflow-hidden border-2 transition-all ${
                    selectedDomains.includes(domain)
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-transparent hover:border-border'
                  }`}
                  onClick={() => {
                    setSelectedDomains((domains) => domains.includes(domain)
                      ? domains.filter((selected) => selected !== domain)
                      : [...domains, domain]);
                    resetPage();
                  }}
                >
                  <img
                    alt={domain}
                    loading="lazy"
                    className="object-cover w-full h-full"
                    src={`https://cdn.piltoverarchive.com/colors/${domain}.webp?width=64`}
                    fetchPriority="low"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/cards/placeholder.jpg";
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Rarity filter as image buttons */}
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-muted-foreground">Rarity:</span>
              {selectedRarities.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedRarities([]);
                    resetPage();
                  }}
                  className="text-xs px-2 py-1 bg-muted rounded-lg hover:bg-muted/80"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                aria-label="All rarities"
                aria-pressed={selectedRarities.length === 0}
                className={`relative size-8 rounded-full overflow-hidden border-2 transition-all ${
                  selectedRarities.length === 0
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-transparent hover:border-border'
                }`}
                onClick={() => {
                  setSelectedRarities([]);
                  resetPage();
                }}
              >
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-xs">All</span>
                </div>
              </button>
              {['Common', 'Uncommon', 'Rare', 'Epic', 'Showcase'].map((rarity) => (
                <button
                  key={rarity}
                  type="button"
                  aria-label={rarity}
                  aria-pressed={selectedRarities.includes(rarity)}
                  className={`relative size-8 rounded-full overflow-hidden border-2 transition-all ${
                    selectedRarities.includes(rarity)
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-transparent hover:border-border'
                  }`}
                  onClick={() => {
                    setSelectedRarities((rarities) => rarities.includes(rarity)
                      ? rarities.filter((selected) => selected !== rarity)
                      : [...rarities, rarity]);
                    resetPage();
                  }}
                >
                  <img
                    alt={rarity}
                    loading="lazy"
                    className="object-cover w-full h-full"
                    src={`https://cdn.piltoverarchive.com/rarities/${rarity.toLowerCase()}.webp?width=32`}
                    fetchPriority="low"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/cards/placeholder.jpg";
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Third row: Other filters */}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <select
              aria-label="Card type"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                resetPage();
              }}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
            >
              <option value="All">All Types</option>
              <option value="Unit">Units</option>
              <option value="Spell">Spells</option>
              <option value="Gear">Gear</option>
              <option value="Battlefield">Battlefields</option>
              <option value="Legend">Legends</option>
              <option value="Rune">Runes</option>
            </select>
            <select
              aria-label="Card set"
              value={selectedSet}
              onChange={(e) => {
                setSelectedSet(e.target.value);
                resetPage();
              }}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
            >
              <option value="All">All Sets</option>
              {uniqueSets.map(set => (
                <option key={set} value={set}>{set}</option>
              ))}
            </select>
            <select
              aria-label="Card supertype"
              value={selectedSupertype}
              onChange={(e) => {
                setSelectedSupertype(e.target.value);
                resetPage();
              }}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
            >
              <option value="All">All Supertypes</option>
              {uniqueSupertypes.map(supertype => (
                <option key={supertype} value={supertype}>{supertype}</option>
              ))}
            </select>
            <select
              aria-label="Card variant"
              value={selectedVariant}
              onChange={(e) => {
                setSelectedVariant(e.target.value);
                resetPage();
              }}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground"
            >
              <option value="All">All Variants</option>
              <option value="Alternate">Alternate Art</option>
              <option value="Signature">Signature</option>
              <option value="Token">Token</option>
            </select>
          </div>
        </div>

        {/* Fourth row: Range sliders */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <RangeSlider
            min={0}
            max={12}
            value={energyRange}
            onChange={(range) => {
              setEnergyRange(range);
              resetPage();
            }}
            label="Energy"
          />
          <RangeSlider
            min={0}
            max={4}
            value={powerRange}
            onChange={(range) => {
              setPowerRange(range);
              resetPage();
            }}
            label="Power"
          />
          <RangeSlider
            min={0}
            max={10}
            value={mightRange}
            onChange={(range) => {
              setMightRange(range);
              resetPage();
            }}
            label="Might"
          />
        </div>
      </div>

      {/* Pagination Controls */}
      {filteredCards.length > cardsPerPage ? (
        <div className="flex justify-center items-center space-x-2 my-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={visiblePage === 1}
            className={`px-3 py-1 rounded ${visiblePage === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
          >
            Previous
          </button>

          <span className="mx-2">
            Page {visiblePage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={visiblePage === totalPages}
            className={`px-3 py-1 rounded ${visiblePage === totalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
          >
            Next
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {cardsToShow.length > 0 ? cardsToShow.map((card, index) => (
          <CardDisplay
            key={card?.api_id || card?.id || `${card?.name || 'card'}-${index}`}
            card={card}
            onClick={setSelectedCard}
          />
        )) : (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            {Array.isArray(initialCards) && initialCards.length > 0
              ? 'No cards match your filters.'
              : 'No cards available.'}
          </div>
        )}
      </div>

      {/* Additional Pagination Controls at Bottom */}
      {filteredCards.length > cardsPerPage ? (
        <div className="flex justify-center items-center space-x-2 my-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={visiblePage === 1}
            className={`px-3 py-1 rounded ${visiblePage === 1 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
          >
            Previous
          </button>

          <span className="mx-2">
            Page {visiblePage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={visiblePage === totalPages}
            className={`px-3 py-1 rounded ${visiblePage === totalPages ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
          >
            Next
          </button>
        </div>
      ) : null}

      {selectedCard && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCard(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="card-inspection-title"
            className="bg-card border border-border/50 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 id="card-inspection-title" className="text-2xl font-bold">
                  {selectedCard?.name || 'Unknown Card'}
                </h3>
                <button
                  type="button"
                  aria-label="Close card inspection"
                  ref={closeDialogButtonRef}
                  className="text-2xl text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedCard(null)}
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="aspect-[2/3] w-full bg-muted/20 flex items-center justify-center rounded-lg overflow-hidden">
                  {getCardImageUrl(selectedCard) ? (
                    <img
                      src={getCardImageUrl(selectedCard)}
                      alt={selectedCard.name || 'Card'}
                      className={`w-full h-full object-cover ${(selectedCard?.type === 'Battlefield' && selectedCard?.orientation === 'landscape') ? '-rotate-90 scale-90' : ''}`}
                      loading="eager"
                      decoding="sync"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      No Image
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-muted-foreground">Type</h4>
                    <p>{selectedCard?.type || 'Unknown'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Energy</h4>
                    <p>{selectedCard?.energy ?? 'N/A'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Might</h4>
                    <p>{selectedCard?.might ?? 'N/A'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Power</h4>
                    <p>{selectedCard?.power ?? 'N/A'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Rarity</h4>
                    <p>{selectedCard?.rarity || 'Unknown'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Set</h4>
                    <p>{selectedCard?.set_label || 'Unknown'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Supertype</h4>
                    <p>{selectedCard?.supertype || 'Unknown'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Domains</h4>
                    <p>{selectedCard?.domains?.join(', ') || 'None'}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-muted-foreground">Tags</h4>
                    <p>{selectedCard?.tags?.join(', ') || 'None'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardBrowser;
